// QA-BATCH-2 · item 10 (2026-09-17) — THE ADDRESS BAR FOLLOWS THE SCREEN, WITHOUT RACING THE ANCHORS.
//
// THE FOUNDER'S FINDING: in-design navigation that goes through a control WITHOUT a minted href (a
// category the contract has no row for, a state change from a map pin, the marketplace's own tabs)
// changes the screen and leaves the address bar on the previous route, so Research → a vendor or a
// category, or Research → Transport, could show one page under another page's URL — and a profile's
// content could survive a route change in the server layer under it.
//
// THE RULING THIS HAS TO RESPECT: WO-URL-NAV-002 (founder 2026-08-18 §7) removed every state-driven
// NAVIGATION from ExportUrlSync because it raced real anchors — measured: the "Transport" anchor's
// ordinary navigation was overtaken by a `location.assign` fired from the state broadcast, and the
// customer landed on the hub instead of the schedule. Route identity lives in the anchor.
//
// SO THIS IS A POLICY, NOT A ROUTER, and it is deliberately pure so it can be tested without a browser:
//   • it never navigates within a mountain — it REPLACES/PUSHES the address to the contract path the
//     current state names (history.pushState, no document load), so the screen and the URL agree at once;
//   • it HOLDS whenever a real anchor was activated moments ago — the browser's own navigation owns
//     that transition and the address it will produce; a push here would only add a duplicate entry;
//   • it HOLDS on any address that is not a design contract path (the booking funnel, /cart, an API),
//     because there the state is a host-owned screen and the URL is the authority;
//   • it HOLDS when the state resolves to nothing or to more than one path (the existing echo guard and
//     ambiguity refusal in resolvePath are unchanged);
//   • a CROSS-MOUNTAIN state (the screen now shows another mountain than the address names) is the one
//     case a push would be a lie — the served commerce facts and head are the old mountain's — so it is
//     a real navigation, guarded by the same anchor window and by a budget of one per document.

export interface UrlSyncInput {
  /** what resolvePath() answered for the broadcast state, or null (ambiguous / unmapped) */
  resolved: string | null;
  /** the current address pathname, trailing slash trimmed */
  here: string;
  /** does `here` itself name a design contract state (stateForPath(here) !== null)? */
  hereIsContract: boolean;
  /** the mountain segment of the resolved path and of `here` (null when the path names none) */
  resolvedMountain: string | null;
  hereMountain: string | null;
  /** the last same-origin anchor activation, if any */
  lastAnchor: { path: string; at: number } | null;
  now: number;
  /** navigations already spent on this document */
  navigationsUsed: number;
  /** the document is unloading (pagehide/beforeunload fired) */
  leaving: boolean;
}

export type UrlSyncDecision =
  | { action: "push"; to: string; why: string }
  | { action: "navigate"; to: string; why: string }
  | { action: "hold"; why: string };

/** How long a real anchor activation owns the address bar. Long enough to cover a slow server
 *  response on a click-driven navigation; short enough that a click that did NOT navigate (an
 *  anchor with an in-page handler) releases the policy on the next state change. */
export const ANCHOR_WINDOW_MS = 4000;

/** `/mountain-<code>/…` → `<code>`; anything else → null. */
export function mountainOfPath(path: string): string | null {
  const code = /^\/mountain-([a-z0-9-]+)(?:\/|$)/i.exec(path)?.[1];
  return code ? code.toLowerCase() : null;
}

export function urlSyncDecision(input: UrlSyncInput): UrlSyncDecision {
  const { resolved, here, hereIsContract, lastAnchor, now, navigationsUsed, leaving } = input;
  if (leaving) return { action: "hold", why: "the document is unloading" };
  if (!resolved) return { action: "hold", why: "the state resolves to no single contract path" };
  if (resolved === here) return { action: "hold", why: "the address already names this state" };
  if (!hereIsContract) return { action: "hold", why: "the address is host-owned, not a design contract path" };
  if (lastAnchor && now - lastAnchor.at >= 0 && now - lastAnchor.at < ANCHOR_WINDOW_MS) {
    return { action: "hold", why: `a real anchor (${lastAnchor.path}) owns this transition` };
  }
  const crossMountain =
    input.resolvedMountain !== null && input.hereMountain !== null && input.resolvedMountain !== input.hereMountain;
  if (crossMountain) {
    if (navigationsUsed >= 1) return { action: "hold", why: "this document has already navigated once" };
    return { action: "navigate", to: resolved, why: "the screen shows another mountain than the address names" };
  }
  return { action: "push", to: resolved, why: "the screen changed within the mountain; the address follows" };
}
